import { CellClassParams, ColDef, ITooltipParams } from 'ag-grid-community';
import { find, isEmpty } from 'lodash';

import validators from 'helpers/validators';
import i18n from 'i18n';
import { convertGender } from 'helpers/users';
import { UserCreationType } from './context/types';

export type UserCreationTypeWithIndex = UserCreationType & { index: number };

export enum UserListError {
    DUPLICATE_EMAIL,
    EXISTING_EMAIL,
    INVALID_EMAIL,
    EMPTY,
}

type RowError = {
    userIndex: number;
    error: UserListError;
};

/**
 * Return a style to be applied on a aggrid cell depending on if it is empty.
 *
 * @param params the aggrid params
 * @param errors the users validation errors
 * @param borderColor the bordercolor to apply.
 * @returns the style to apply.
 */
export const emptyCellStyle = (
    params: CellClassParams<UserCreationTypeWithIndex>,
    errors: RowError[],
    borderColor: string,
): { borderColor?: string } => {
    const error = find(
        errors,
        (err) =>
            err.userIndex === params?.node?.data?.index &&
            err.error === UserListError.EMPTY,
    );

    if (error !== undefined && isEmpty(params.value)) {
        return {
            borderColor,
        };
    }
    return {};
};

/**
 * Return a tooltip to be shown on a aggrid cell depending on if it is empty.
 *
 * @param params the aggrid params
 * @param errors the users validation errors
 * @param tooltip the tooltip to show if it is empty.
 * @returns the tooltip.
 */
export const emptyTooltipValueGetter = (
    params: ITooltipParams<UserCreationTypeWithIndex>,
    errors: RowError[],
    tooltip: string,
) => {
    const error = find(
        errors,
        (err) =>
            err.userIndex === params?.node?.data?.index &&
            err.error === UserListError.EMPTY,
    );
    if (error !== undefined && isEmpty(params.value)) {
        return tooltip;
    }
    return undefined;
};

const hasDuplicateEmail = (
    email: string | undefined,
    allEmails: string[],
): boolean =>
    allEmails.filter((e) => e.toLowerCase() === email?.toLowerCase()).length >=
    2;

/**
 * Validate an user list in the context of the form to create them.
 * Will return errors per row.
 *
 * @param users the list of user with index.
 * @param existingEmail The list of already used email.
 * @param newEmails The list of email that will be created.
 */
export const validateUsersList = (
    users: UserCreationTypeWithIndex[],
    existingEmail: string[],
    newEmails: string[],
): RowError[] => {
    const errors: RowError[] = [];
    users.forEach((user) => {
        // validate email.
        if (!validators.isEmail(user.email)) {
            errors.push({
                userIndex: user.index,
                error: UserListError.INVALID_EMAIL,
            });
            // check for duplicate email.
        } else if (
            hasDuplicateEmail(user.email, [user.email, ...existingEmail])
        ) {
            errors.push({
                userIndex: user.index,
                error: UserListError.EXISTING_EMAIL,
            });
        } else if (hasDuplicateEmail(user.email, newEmails)) {
            errors.push({
                userIndex: user.index,
                error: UserListError.DUPLICATE_EMAIL,
            });
        }

        // check if line has been edited and there is empty cell.
        const hasEmptyCell =
            isEmpty(user.email) ||
            isEmpty(user.firstname) ||
            isEmpty(user.lastname) ||
            isEmpty(user.job);

        const hasBeenEdited =
            !isEmpty(user.email) ||
            !isEmpty(user.firstname) ||
            !isEmpty(user.lastname) ||
            !isEmpty(user.job);

        if (hasEmptyCell && hasBeenEdited) {
            errors.push({
                userIndex: user.index,
                error: UserListError.EMPTY,
            });
        }
    });

    return errors;
};

/**
 * Helper to create ColDef for the user creation list.
 * @param field the user field to create the coldef for.
 * @param updateUser the function to call to update the user.
 * @param flex a flex value for the column if desired.
 */
export const usersListColumnDef = (
    field: keyof UserCreationTypeWithIndex,
    updateUser: (
        index: number,
        field: keyof UserCreationTypeWithIndex,
        newValue: string,
    ) => void,
    flex: number | undefined = undefined,
): ColDef => {
    const valueGetter = ({ data }: { data: UserCreationTypeWithIndex }) =>
        data[field];
    const valueSetter = ({
        data,
        newValue,
    }: {
        data: UserCreationTypeWithIndex;
        newValue: string;
    }) => {
        updateUser(data.index, field, newValue);
        return true;
    };

    return {
        field,
        headerName: i18n.t(`common.${field}`),
        valueGetter,
        valueSetter,
        ...(flex !== undefined ? { flex } : {}),
    };
};

type UCTEditableField = Pick<
    UserCreationType,
    'gender' | 'lastname' | 'firstname' | 'job' | 'email'
>;

export type UCTKey = keyof UCTEditableField;

const fieldMap: { [key: number]: UCTKey } = {
    0: 'gender',
    1: 'lastname',
    2: 'firstname',
    3: 'job',
    4: 'email',
};

const offsetMap: { [key: string]: number } = {
    gender: 0,
    lastname: 1,
    firstname: 2,
    job: 3,
    email: 4,
};

/**
 * Set the user field according to an array of string, from the `columnId`, assuming the order
 * declared in `fieldMap`, i.d ["gender", "lastname", "firstname", "job", "email"].
 *
 * So, for exemple, if `data = ["M", "Truc"]` and `columnId = "gender"`,
 * then the field "gender" in user will be set with "M" and the field "lastname" with "Truc".
 *
 * Another exemple, if `data = ["chief", "e@mail.fr", "waste"]` and `columnId = "job"` then:
 * the field "job" will be set to "chief", "email" to "e@mail.fr" and the last datapoint will be ignore
 * since it overflow our field definition.
 *
 * @param user In which the field are to be set
 * @param data The tabular value to be set in the user
 * @param columnId From which column should we start to set the data.
 */
export const mergeUserAndDataFromColumn = (
    user: Partial<UserCreationType>,
    data: string[],
    columnId: UCTKey,
): Partial<UserCreationType> => {
    const newUser = { ...user };
    const offset = offsetMap[columnId];
    for (let i = 0; i + offset < 5; i += 1) {
        if (i >= data.length) {
            break;
        }
        const field = fieldMap[offset + i] as UCTKey;
        if (field === 'gender') {
            newUser[field] = convertGender(data[i]);
        } else {
            newUser[field] = data[i];
        }
    }
    return newUser;
};
